// src/hooks/usePlan.js
// Reads subscription_plan from companies table using the logged-in user's company
// Matches the same auth pattern used in Dashboard.jsx

import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from '../supabaseClient';

// ─── Plan Config ─────────────────────────────────────────────
export const PLAN_CONFIG = {
  demo: {
  label: 'Demo',
  modules: {
    dashboard:               'full',
    receivables:             'full',
    payables:                'full',
    hedges:                  'full',
    cashInBank:              'full',
    excelUpload:             true,
    marketIntelligence:      true,
    debtManagement:          true,
    investments:             true,
    aiInsights:              'advanced',
    receivablesIntelligence: true,
    treasuryReporting:       true,
    importFinancing: true,
  }
},
  core: {
    label: 'Core',
    modules: {
      dashboard:                'full',
      receivables:              'full',
      payables:                 'full',
      hedges:                   'full',
      cashInBank:               'full',
      excelUpload:              true,
      marketIntelligence:       true,
      debtManagement:           true,
      investments:              true,
      aiInsights:               'basic',
      receivablesIntelligence:  false,   // Pro only
      treasuryReporting:        false,   // Pro only
      importFinancing: false,   // Pro only
    }
  },
  pro: {
    label: 'Pro',
    modules: {
      dashboard:                'full',
      receivables:              'full',
      payables:                 'full',
      hedges:                   'full',
      cashInBank:               'full',
      excelUpload:              true,
      marketIntelligence:       true,
      debtManagement:           true,
      investments:              true,
      aiInsights:               'advanced',
      receivablesIntelligence:  true,
      treasuryReporting:        true,
      importFinancing: true,
    }
  }
};

// ─── Context ──────────────────────────────────────────────────
const PlanContext = createContext(null);

export function PlanProvider({ children }) {
  const [plan,           setPlan]           = useState('demo');
  const [trialStartedAt, setTrialStartedAt] = useState(null);
  const [loading,        setLoading]        = useState(true);

  useEffect(() => {
    fetchPlan();
  }, []);

  async function fetchPlan() {
    try {
      // Step 1: get logged-in user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      // Step 2: get company_id from company_users (same as Dashboard.jsx)
      const { data: compUser, error: compErr } = await supabase
        .from('company_users')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (compErr || !compUser) { setLoading(false); return; }

      // Step 3: get plan from companies table
      const { data, error } = await supabase
        .from('companies')
        .select('subscription_plan, trial_started_at')
        .eq('id', compUser.company_id)
        .single();

      if (error) throw error;

      setPlan(data.subscription_plan || 'demo');
      setTrialStartedAt(data.trial_started_at);
      setPlan(data.subscription_plan || 'demo');
setTrialStartedAt(data.trial_started_at);
    } catch (err) {
      console.error('Error fetching plan:', err);
      setPlan('demo');
    } finally {
      setLoading(false);
    }
  }

  // ─── Trial calculations ────────────────────────────────────
  const trialDaysUsed = trialStartedAt
    ? Math.floor((Date.now() - new Date(trialStartedAt)) / (1000 * 60 * 60 * 24))
    : 0;

  const trialDaysRemaining = Math.max(0, 30 - trialDaysUsed);
  const isTrialExpired     = plan === 'demo' && trialDaysUsed >= 30;
  const showTrialWarning   = plan === 'demo' && trialDaysUsed >= 15 && !isTrialExpired;

  // ─── Access helpers ────────────────────────────────────────
 function canAccess(moduleKey) {
  // Expired demo — lock everything except basics
  if (plan === 'demo' && isTrialExpired) {
    const basicModules = ['dashboard', 'receivables', 'payables', 'hedges', 'cashInBank', 'excelUpload'];
    return basicModules.includes(moduleKey);
  }
  // Active demo, core, pro — check plan config normally
  const access = PLAN_CONFIG[plan]?.modules[moduleKey];
  return access !== false && access !== undefined;
}

  function getAccess(moduleKey) {
    return PLAN_CONFIG[plan]?.modules[moduleKey] ?? false;
  }

  function requiredPlanFor(moduleKey) {
    if (PLAN_CONFIG.core.modules[moduleKey]) return 'core';
    return 'pro';
  }

  return (
    <PlanContext.Provider value={{
      plan,
      loading,
      trialDaysUsed,
      trialDaysRemaining,
      isTrialExpired,
      showTrialWarning,
      canAccess,
      getAccess,
      requiredPlanFor,
      refreshPlan: fetchPlan,
    }}>
      {children}
    </PlanContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────
export function usePlan() {
  const context = useContext(PlanContext);
  if (!context) throw new Error('usePlan must be used inside PlanProvider');
  return context;
}
